import {Component, effect, input, output, signal} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Dialog} from 'primeng/dialog';
import {FloatLabel} from 'primeng/floatlabel';
import {InputText} from 'primeng/inputtext';
import {Message} from 'primeng/message';
import {InputGroup} from 'primeng/inputgroup';
import {InputGroupAddon} from 'primeng/inputgroupaddon';
import {InputMask} from 'primeng/inputmask';
import {Button} from 'primeng/button';
import {NgIcon, provideIcons} from '@ng-icons/core';
import {tablerCashBanknote, tablerClock, tablerX} from '@ng-icons/tabler-icons';
import {EndingInfo, Picture} from '../auctions-offer.types';
import {NgClass} from '@angular/common';
import {Checkbox} from 'primeng/checkbox';
import {environment} from '../../../../environments/environment';

export interface CarOfferData {
  id: string;
  name: string;
  offerGallery: Picture[];
  installment: number;
  offerEndingInfo: EndingInfo;
}

type CarOfferFormControls = {
  fullName: FormControl<string>;
  company: FormControl<string>;
  email: FormControl<string>;
  phone: FormControl<string>;
  consentDataProcessing: FormControl<boolean>;
  consentPrivacyPolicy: FormControl<boolean>;
};

export interface CarOfferFormData {
  offerId: string;
  carName: string;
  carGallery: Picture[];
  carInstallment: number;
  offerDaysLeft: string;
  fullName: string;
  company: string;
  email: string;
  phone: string;
  consentDataProcessing: boolean;
  consentPrivacyPolicy: boolean;
}

@Component({
  selector: 'app-auctions-offer-modal',
  imports: [
    Dialog,
    FloatLabel,
    InputText,
    ReactiveFormsModule,
    Message,
    InputGroup,
    InputGroupAddon,
    InputMask,
    Button,
    NgIcon,
    NgClass,
    Checkbox
  ],
  templateUrl: './auctions-offer-modal.component.html',
  styleUrl: './auctions-offer-modal.component.scss',
  providers: [
    provideIcons({tablerClock, tablerCashBanknote, tablerX})
  ]
})
export class AuctionsOfferModalComponent {
  carData = input.required<CarOfferData>();
  visible = signal(true);
  isProcessing = signal(false);

  formSubmit = output<CarOfferFormData>();
  modalClose = output<void>();

  protected readonly privacyPolicyUrl = environment.privacyPolicyUrl;
  protected readonly phoneRegex = '^\\d{3}[\\s-]?\\d{3}[\\s-]?\\d{3}$';

  constructor() {
    effect(() => {
      if (this.isProcessing()) {
        this.offerForm.disable({emitEvent: false});
      } else {
        this.offerForm.enable({emitEvent: false});
      }
    });
  }

  readonly offerForm = new FormGroup<CarOfferFormControls>({
    fullName: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(2), Validators.maxLength(50)],
    }),
    company: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(80)],
    }),
    email: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email, Validators.maxLength(50)],
    }),
    phone: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.pattern(this.phoneRegex), Validators.maxLength(50)],
    }),
    consentDataProcessing: new FormControl<boolean>(false, {
      nonNullable: true,
      validators: Validators.requiredTrue
    }),
    consentPrivacyPolicy: new FormControl<boolean>(false, {
      nonNullable: true,
      validators: Validators.requiredTrue
    })
  });

  isInvalid(name: keyof CarOfferFormControls) {
    const ctrl = this.offerForm.controls[name];
    return (ctrl.touched || this.isProcessing()) && ctrl.invalid;
  }

  isError(name: keyof CarOfferFormControls): string | null {
    const form = this.offerForm.controls[name];
    if (!(form.touched || this.isProcessing()) || form.valid) return null;
    if (form.hasError('required')) return 'Pole jest wymagane';
    if (form.hasError('minlength')) return 'Za krótka wartość';
    if (form.hasError('maxlength')) return 'Za długa wartość';
    if (form.hasError('email')) return 'Nieprawidłowy format email';
    if (form.hasError('pattern')) return 'Nieprawidłowy format telefonu';

    return 'Nieprawidłowy format';
  }

  onClose() {
    this.visible.set(false);
    this.modalClose.emit();
  }

  onSubmit() {
    if (this.offerForm.invalid) {
      this.offerForm.markAllAsTouched();
      return;
    }

    this.isProcessing.set(true);

    const formData: CarOfferFormData = {
      offerId: this.carData().id,
      carName: this.carData().name,
      carGallery: this.carData().offerGallery,
      carInstallment: this.carData().installment,
      offerDaysLeft: this.carData().offerEndingInfo.daysLeft,
      fullName: this.offerForm.controls.fullName.value,
      company: this.offerForm.controls.company.value,
      email: this.offerForm.controls.email.value,
      phone: this.offerForm.controls.phone.value,
      consentDataProcessing: this.offerForm.controls.consentDataProcessing.value,
      consentPrivacyPolicy: this.offerForm.controls.consentPrivacyPolicy.value
    };

    this.formSubmit.emit(formData);

    setTimeout(() => {
      this.isProcessing.set(false);
      this.offerForm.reset();
      this.onClose();
    }, 1000);
  }

  protected getImageUrl(offerId: string, imageId: string): string {
    return `https://api.licytacje.processway.pl/public/listing/${offerId}/pictures/${imageId}?scaleType=scale&scaleHeight=500&scaleWidth=500`;
  }
}
